import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { empresasService, Empresa } from '../src/services/empresasService';

interface EmpresasProps {
  onClose: () => void;
}

type SortDirection = 'asc' | 'desc';
type SortableEmpresaKey = 'nombreEmpresa' | 'fechaCreacion' | 'activa';

interface EmpresaFormData {
  nombreEmpresa: string;
  activa: boolean;
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm
  licenciaActiva: boolean;
  fechaExpiracionLicencia: string; // '' means null
  fechaUltimoPago: string; // datetime-local string or ''
  planActual: string;
  notasLicencia: string;
}

const Empresas: React.FC<EmpresasProps> = ({ onClose }) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);
  const [formData, setFormData] = useState<EmpresaFormData>({
    nombreEmpresa: '',
    activa: true,
    horaInicio: '07:00',
    horaFin: '23:30',
    licenciaActiva: true,
    fechaExpiracionLicencia: '',
    fechaUltimoPago: '',
    planActual: '',
    notasLicencia: ''
  });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortableEmpresaKey; direction: SortDirection } | null>(null);

  useEffect(() => {
    loadEmpresas();
  }, []);

  // Inject CSS styles for modal animations (crecer / disminuir)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(0.001); opacity: 0; }
      }
      @keyframes crecer {
        0% { transform: scale(0.001); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await empresasService.getAll();
      setEmpresas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortableEmpresaKey) => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredEmpresas = useMemo(() => {
    const loweredTerm = searchTerm.toLowerCase();
    let filtered = empresas.filter(e => e.nombreEmpresa.toLowerCase().includes(loweredTerm));
    if (sortConfig) {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      filtered = [...filtered].sort((a, b) => {
        if (sortConfig.key === 'activa') {
          return (a.activa === b.activa ? 0 : a.activa ? -1 : 1) * direction;
        }
        const aValue = a[sortConfig.key] as any;
        const bValue = b[sortConfig.key] as any;
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    }
    return filtered;
  }, [empresas, searchTerm, sortConfig]);

  const handleCreate = () => {
    setEditingEmpresa(null);
    setFormData({
      nombreEmpresa: '',
      activa: true,
      horaInicio: '07:00',
      horaFin: '23:30',
      licenciaActiva: true,
      fechaExpiracionLicencia: '',
      fechaUltimoPago: '',
      planActual: '',
      notasLicencia: ''
    });
    setShowForm(true);
  };

  const handleEdit = (empresa: Empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombreEmpresa: empresa.nombreEmpresa,
      activa: empresa.activa,
      horaInicio: (empresa.horaInicio || '07:00').substring(0, 5),
      horaFin: (empresa.horaFin || '23:30').substring(0, 5),
      licenciaActiva: empresa.licenciaActiva ?? true,
      fechaExpiracionLicencia: empresa.fechaExpiracionLicencia ? empresa.fechaExpiracionLicencia.slice(0, 10) : '',
      fechaUltimoPago: empresa.fechaUltimoPago ? empresa.fechaUltimoPago.slice(0, 16) : '',
      planActual: empresa.planActual || '',
      notasLicencia: empresa.notasLicencia || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number, nombre: string) => {
    const res = await Swal.fire({
      title: '¿Eliminar empresa?',
      text: `Esta acción desactivará la empresa "${nombre}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false
    });
    if (!res.isConfirmed) return;
    try {
      await empresasService.delete(id);
      setEmpresas(prev => prev.filter(e => e.empresaId !== id));
      await Swal.fire({
        title: 'Empresa eliminada',
        text: 'La empresa ha sido desactivada.',
        icon: 'success',
        background: 'rgba(15,23,42,0.85)',
        color: '#fff',
        confirmButtonColor: '#3b82f6',
        backdrop: false
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al eliminar';
      setError(message);
      Swal.fire({ title: 'Error', text: message, icon: 'error', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#ef4444', backdrop: false });
    }
  };

  const handleToggleStatus = async (empresa: Empresa) => {
    const action = empresa.activa ? 'desactivar' : 'activar';
    const res = await Swal.fire({
      title: `¿Deseas ${action} esta empresa?`,
      text: `La empresa "${empresa.nombreEmpresa}" será ${empresa.activa ? 'ocultada' : 'reactivada'}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false
    });
    if (!res.isConfirmed) return;
    try {
      const updated = await empresasService.update(empresa.empresaId, {
        nombreEmpresa: empresa.nombreEmpresa,
        activa: !empresa.activa,
        licenciaActiva: empresa.licenciaActiva,
        fechaExpiracionLicencia: empresa.fechaExpiracionLicencia,
        fechaUltimoPago: empresa.fechaUltimoPago,
        planActual: empresa.planActual,
        notasLicencia: empresa.notasLicencia,
        horaInicio: empresa.horaInicio,
        horaFin: empresa.horaFin,
      });
      setEmpresas(prev => prev.map(it => it.empresaId === updated.empresaId ? updated : it));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No pudimos actualizar el estado. Inténtalo nuevamente.';
      setError(message);
      Swal.fire({ title: 'Error', text: message, icon: 'error', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#ef4444', backdrop: false });
    }
  };

  const toSeconds = (hhmm: string) => (hhmm && hhmm.length === 5 ? `${hhmm}:00` : hhmm);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingEmpresa) {
        const updated = await empresasService.update(editingEmpresa.empresaId, {
          nombreEmpresa: formData.nombreEmpresa,
          activa: formData.activa,
          licenciaActiva: formData.licenciaActiva,
          fechaExpiracionLicencia: formData.fechaExpiracionLicencia ? formData.fechaExpiracionLicencia : null,
          fechaUltimoPago: formData.fechaUltimoPago ? new Date(formData.fechaUltimoPago).toISOString() : null,
          planActual: formData.planActual ? formData.planActual : null,
          notasLicencia: formData.notasLicencia ? formData.notasLicencia : null,
          horaInicio: toSeconds(formData.horaInicio),
          horaFin: toSeconds(formData.horaFin),
        });
        setEmpresas(prev => prev.map(e => e.empresaId === updated.empresaId ? updated : e));
        await Swal.fire({
          title: 'Empresa actualizada',
          text: 'Se han guardado los cambios correctamente.',
          icon: 'success',
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonColor: '#3b82f6',
          backdrop: false
        });
      } else {
        const created = await empresasService.create({
          nombreEmpresa: formData.nombreEmpresa,
          activa: formData.activa,
          licenciaActiva: true,
          fechaExpiracionLicencia: null,
          planActual: null,
          notasLicencia: null,
          horaInicio: toSeconds(formData.horaInicio),
          horaFin: toSeconds(formData.horaFin),
        });
        setEmpresas(prev => [created, ...prev]);
        await Swal.fire({
          title: 'Empresa creada',
          text: 'La empresa fue registrada correctamente.',
          icon: 'success',
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          confirmButtonColor: '#3b82f6',
          backdrop: false
        });
      }
      setIsModalClosing(true);
      setTimeout(() => {
        setShowForm(false);
        setIsModalClosing(false);
        setEditingEmpresa(null);
      }, 300);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al guardar';
      setError(message);
      Swal.fire({ title: 'Error', text: message, icon: 'error', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#ef4444', backdrop: false });
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX');
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 flex items-start justify-center bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="w-full max-w-6xl px-2 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors">
              <span className="material-symbols-outlined">arrow_back</span>
              Volver
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-2xl">apartment</span>
            <h2 className="text-2xl text-secondary dark:text-white">Empresas</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {!showForm ? (
            <>
              {/* Toolbar */}
              <div className="bg-[#307CAF] text-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-200">search</span>
                      <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-transparent rounded-full bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary dark:bg-slate-800 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>{filteredEmpresas.length} empresas</span>
                    <button onClick={loadEmpresas} className="inline-flex items-center gap-1 px-3 py-1 border border-white/20 rounded-full hover:bg-white/10 transition" title="Recargar">
                      <span className="material-symbols-outlined text-base">refresh</span>
                      Recargar
                    </button>
                    <button onClick={handleCreate} className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#307CAF] shadow hover:bg-slate-50 transition-colors">
                      <span className="material-symbols-outlined text-base">add</span>
                      Nueva Empresa
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              {loading ? (
                <div className="p-12 text-center text-slate-500 dark:text-slate-400">Cargando empresas...</div>
              ) : filteredEmpresas.length === 0 ? (
                <div className="p-12 text-center text-slate-500 dark:text-slate-400">No se encontraron empresas con ese criterio.</div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead className="bg-[#307CAF] text-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th onClick={() => handleSort('nombreEmpresa')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Empresa
                              {sortConfig?.key === 'nombreEmpresa' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Estado</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Licencia</th>
                          <th onClick={() => handleSort('fechaCreacion')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-slate-100/20">
                            <div className="flex items-center gap-1">
                              Fecha de registro
                              {sortConfig?.key === 'fechaCreacion' && (
                                <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-light dark:divide-border-dark">
                        {filteredEmpresas.map((empresa) => (
                          <tr key={empresa.empresaId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-slate-900 dark:text-white">{empresa.nombreEmpresa}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">ID #{empresa.empresaId}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${empresa.activa ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'}`}>
                                <span className="material-symbols-outlined text-sm">{empresa.activa ? 'verified' : 'visibility_off'}</span>
                                {empresa.activa ? 'Activa' : 'Inactiva'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${empresa.licenciaActiva ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'}`}>
                                <span className="material-symbols-outlined text-sm">{empresa.licenciaActiva ? 'verified_user' : 'block'}</span>
                                {empresa.licenciaActiva ? 'Activa' : 'Bloqueada'}
                              </span>
                              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{empresa.fechaExpiracionLicencia ? `Expira: ${formatDate(empresa.fechaExpiracionLicencia)}` : 'Sin expiración'}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{formatDate(empresa.fechaCreacion)}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleEdit(empresa)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50">
                                  <span className="material-symbols-outlined text-sm">edit</span>
                                  Editar
                                </button>
                                <button onClick={() => handleToggleStatus(empresa)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-primary hover:bg-primary/10">
                                  <span className="material-symbols-outlined text-sm">{empresa.activa ? 'toggle_off' : 'toggle_on'}</span>
                                  {empresa.activa ? 'Desactivar' : 'Activar'}
                                </button>
                                <button onClick={() => handleDelete(empresa.empresaId, empresa.nombreEmpresa)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  <span className="material-symbols-outlined text-sm">delete</span>
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Form */
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
              <div className={`relative w-full max-w-2xl transform rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl transition-all ${isModalClosing ? 'animate-[disminuir_0.3s_ease-in_forwards]' : 'animate-[crecer_0.3s_ease-out_forwards]'}`}>
                <div className="rounded-2xl backdrop-blur-sm bg-white/50 dark:bg-slate-800 p-6 shadow-xl dark:border-slate-700 max-h-[90vh] overflow-y-auto">
                  <button onClick={() => { setIsModalClosing(true); setTimeout(() => { setShowForm(false); setIsModalClosing(false); setEditingEmpresa(null); }, 300); }} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
                    <span className="material-symbols-outlined">close</span>
                  </button>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingEmpresa ? 'Editar empresa' : 'Registrar empresa'}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Añade los datos básicos de la empresa.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre de la empresa</label>
                        <input type="text" required value={formData.nombreEmpresa} onChange={(e) => setFormData(prev => ({ ...prev, nombreEmpresa: e.target.value }))} maxLength={100} className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Ej. Consultorios Centro" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Estado</label>
                        <label className="inline-flex items-center cursor-pointer">
                          <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.activa ? 'Activa' : 'Inactiva'}</span>
                          <input type="checkbox" className="sr-only" checked={formData.activa} onChange={(e) => setFormData(prev => ({ ...prev, activa: e.target.checked }))} />
                          <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.activa ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                            <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.activa ? 'translate-x-5' : ''}`} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {editingEmpresa && (
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Licencia</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Controla el acceso de la empresa al sistema.</p>
                          </div>
                          <label className="inline-flex items-center cursor-pointer">
                            <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.licenciaActiva ? 'Activa' : 'Bloqueada'}</span>
                            <input type="checkbox" className="sr-only" checked={formData.licenciaActiva} onChange={(e) => setFormData(prev => ({ ...prev, licenciaActiva: e.target.checked }))} />
                            <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.licenciaActiva ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                              <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.licenciaActiva ? 'translate-x-5' : ''}`} />
                            </span>
                          </label>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Plan actual</label>
                            <select value={formData.planActual} onChange={(e) => setFormData(prev => ({ ...prev, planActual: e.target.value }))} className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary">
                              <option value="">Seleccionar plan</option>
                              <option value="Mensual">Mensual</option>
                              <option value="Anual">Anual</option>
                              <option value="Prueba30">Prueba30</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Fecha expiración</label>
                              <input type="date" value={formData.fechaExpiracionLicencia} onChange={(e) => setFormData(prev => ({ ...prev, fechaExpiracionLicencia: e.target.value }))} className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" />
                              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Vacío = sin límite</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Último pago</label>
                              <input type="datetime-local" value={formData.fechaUltimoPago} onChange={(e) => setFormData(prev => ({ ...prev, fechaUltimoPago: e.target.value }))} className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Notas</label>
                            <textarea value={formData.notasLicencia} onChange={(e) => setFormData(prev => ({ ...prev, notasLicencia: e.target.value }))} maxLength={500} rows={3} className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Motivo de bloqueo, referencia de pago, etc." />
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-base text-slate-900 dark:text-slate-400 font-bold pt-4">Horario de atención</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#307CAF] dark:text-slate-300 mb-2">Hora inicial</label>
                        <input type="time" value={formData.horaInicio} onChange={(e) => setFormData(prev => ({ ...prev, horaInicio: e.target.value }))} className="w-full rounded-full bg-white/80 dark:bg-slate-700/60 px-4 py-2 text-center text-slate-900 dark:text-white focus:outline-none" step={60} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#307CAF] dark:text-slate-300 mb-2">Hora final</label>
                        <input type="time" value={formData.horaFin} onChange={(e) => setFormData(prev => ({ ...prev, horaFin: e.target.value }))} className="w-full rounded-full bg-white/80 dark:bg-slate-700/60 px-4 py-2 text-center text-slate-900 dark:text-white focus:outline-none" step={60} />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button type="button" className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50" onClick={() => { setIsModalClosing(true); setTimeout(() => { setShowForm(false); setIsModalClosing(false); setEditingEmpresa(null); }, 300); }}>
                        Cancelar
                      </button>
                      <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-hover disabled:opacity-60">
                        {saving ? (
                          <>
                            <span className="material-symbols-outlined animate-spin">progress_activity</span>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined">save</span>
                            Guardar
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Empresas;
